import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ListGroup, ListGroupItem, Panel } from 'react-bootstrap';
import { useStateValue } from '../context/state.jsx';
import { closeOpenProject, focusProject, sourceFormatLabel } from '../util/openProject.js';

const ProjectList = ({
	currentDataset,
	setIsFlyoutLeftActive
}) => {
	const [{ datasets, openDatasets }, dispatch] = useStateValue();
	const navigate = useNavigate();
	const datasetMap = datasets && typeof datasets === 'object' ? datasets : {};
	const focusedId = currentDataset || null;
	const openIds = (openDatasets || []).map(String).filter(id => datasetMap[id]);
	const libraryIds = Object.keys(datasetMap).filter(id => !openIds.includes(String(id)));

	const openOrFocus = (event, datasetId, closeFlyout) => {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		if (closeFlyout) {
			setIsFlyoutLeftActive(false);
		}
		focusProject({ dispatch, navigate, datasetId, datasets: datasetMap });
	};

	const closeProject = (event, datasetId) => {
		event.preventDefault();
		event.stopPropagation();
		closeOpenProject({
			dispatch,
			navigate,
			datasetId,
			openDatasets: openIds,
			focusedDatasetId: focusedId,
			datasets: datasetMap
		});
	};

	const renderOpenItem = datasetId => {
		const dataset = datasetMap[datasetId];
		const isFocused = String(focusedId) === String(datasetId);
		return (
			<div
				key={datasetId}
				className={classnames('list-group-item projectListItem projectListItem-actions', { active: isFocused })}>
				<div className="projectListItemMain">
					<button
						type="button"
						className="projectListNameBtn"
						onClick={event => openOrFocus(event, datasetId, true)}>
						<span className="openDot" aria-hidden="true"></span>
						{dataset.Name}
						<span className="sourceFormatBadge">{sourceFormatLabel(dataset)}</span>
						{dataset.p6ProjectNotice ? (
							<span className="projectListChip" title={dataset.p6ProjectNotice}>One P6 project</span>
						) : null}
					</button>
				</div>
				<div className="projectListItemTools">
					{!isFocused
						? (
							<button
								type="button"
								className="projectListChip"
								onClick={event => openOrFocus(event, datasetId, true)}>
								Focus
							</button>
						)
						: null}
					<Link
						className="projectListChip"
						to={`/setup/projects/${datasetId}`}
						title="Configure"
						onClick={() => setIsFlyoutLeftActive(false)}>
						<span className="glyphicon glyphicon-cog"></span>
					</Link>
					<button
						type="button"
						className="projectListChip"
						title="Close"
						onClick={event => closeProject(event, datasetId)}>
						×
					</button>
				</div>
			</div>
		);
	};

	const renderLibraryItem = datasetId => {
		const dataset = datasetMap[datasetId];
		return (
			<div key={datasetId} className="list-group-item projectListItem projectListItem-actions">
				<div className="projectListItemMain">
					<span className="projectListName">{dataset.Name}</span>
					<span className="sourceFormatBadge">{sourceFormatLabel(dataset)}</span>
					{dataset.p6ProjectNotice ? (
						<span className="projectListChip" title={dataset.p6ProjectNotice}>One P6 project</span>
					) : null}
				</div>
				<div className="projectListItemTools">
					<button
						type="button"
						className="projectListChip projectListChip-primary"
						onClick={event => openOrFocus(event, datasetId, true)}>
						Open
					</button>
					<Link
						className="projectListChip"
						to={`/setup/projects/${datasetId}`}
						title="Configure"
						onClick={() => setIsFlyoutLeftActive(false)}>
						<span className="glyphicon glyphicon-cog"></span>
					</Link>
				</div>
			</div>
		);
	};

	return (
		<div className="projectListGroups">
			<Panel defaultExpanded>
				<Panel.Heading>
					<Panel.Title toggle>Open now ({openIds.length})</Panel.Title>
				</Panel.Heading>
				<Panel.Collapse>
					<ListGroup className="projectListGroup">
						{openIds.length
							? openIds.map(renderOpenItem)
							: <ListGroupItem className="projectListEmpty">No projects open. Open one from Library.</ListGroupItem>}
					</ListGroup>
				</Panel.Collapse>
			</Panel>
			<Panel defaultExpanded={openIds.length === 0}>
				<Panel.Heading>
					<Panel.Title toggle>Library ({libraryIds.length})</Panel.Title>
				</Panel.Heading>
				<Panel.Collapse>
					<ListGroup className="projectListGroup">
						{libraryIds.length
							? libraryIds.map(renderLibraryItem)
							: <ListGroupItem className="projectListEmpty">All projects are already open.</ListGroupItem>}
					</ListGroup>
				</Panel.Collapse>
			</Panel>
		</div>
	);
};

ProjectList.propTypes = {
	currentDataset: PropTypes.string,
	setIsFlyoutLeftActive: PropTypes.func
};

export default ProjectList;
